import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/lib/auth";
import { useStore } from "@/lib/store";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  Sparkles, LogOut, User, Award, Layers, Repeat, Package,
  Play, Pause, X as CloseIcon, Loader2, Calendar
} from "lucide-react";

const TABS = [
  { key: "profile", label: "Profile", icon: User },
  { key: "wardrobe", label: "Wardrobe", icon: Layers },
  { key: "loyalty", label: "Loyalty", icon: Award },
  { key: "subscriptions", label: "Subscriptions", icon: Repeat },
  { key: "orders", label: "Orders", icon: Package },
];

function TierBadge({ tier }) {
  const map = {
    "Silver Mist": "from-slate-300 to-slate-400 text-slate-900",
    "Golden Amber": "from-[#D4B278] to-[#C5A059] text-[#0B0A08]",
    "Obsidian Extrait": "from-[#2C1810] to-[#000] text-[#D4B278] border border-[#D4B278]/40",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] uppercase tracking-[0.16em] bg-gradient-to-br ${map[tier] || map["Silver Mist"]}`}>
      <Sparkles className="h-3 w-3" /> {tier}
    </span>
  );
}

export default function AccountPage() {
  const { user, isAuthenticated, logout, refreshUser } = useAuth();
  const { state, format } = useStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState(new URLSearchParams(location.search).get("tab") || "profile");
  const [loyalty, setLoyalty] = useState(null);
  const [wardrobe, setWardrobe] = useState([]);
  const [subs, setSubs] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [lRes, wRes, sRes, oRes] = await Promise.all([
          api.get("/account/loyalty"),
          api.get("/account/wardrobe"),
          api.get("/account/subscriptions"),
          api.get("/account/orders"),
        ]);
        if (cancelled) return;
        setLoyalty(lRes.data);
        setWardrobe(wRes.data);
        setSubs(sRes.data);
        setOrders(oRes.data);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[account] load failed", err?.response?.status);
        if (!cancelled) toast.error("Could not load account data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: "/account" }} replace />;
  }

  const handleLogout = () => { logout(); toast.success("Signed out"); navigate("/"); };

  const patchSub = async (id, patch) => {
    try {
      await api.patch(`/account/subscriptions/${id}`, patch);
      const s = await api.get("/account/subscriptions");
      setSubs(s.data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[account] patchSub failed", err?.response?.status);
      toast.error("Could not update subscription");
    }
  };

  const cancelSub = async (id) => {
    try {
      await api.delete(`/account/subscriptions/${id}`);
      const s = await api.get("/account/subscriptions");
      setSubs(s.data);
      toast.success("Subscription cancelled");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[account] cancelSub failed", err?.response?.status);
      toast.error("Could not cancel");
    }
  };

  const profile = state.quizProfile;

  return (
    <main data-testid="account-portal" className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-10 py-10 sm:py-14">
      {/* Header block */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 sm:mb-10">
        <div>
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-[#C5A059] mb-2">Scentodore Society</div>
          <h1 className="font-serif text-3xl sm:text-5xl leading-[1.05]">{user?.full_name || "Welcome"}</h1>
          <div className="mt-3 flex items-center gap-3">
            {loyalty && <TierBadge tier={loyalty.tier} />}
            <span className="text-sm text-[#1A1918]/70">{loyalty?.points ?? 0} points</span>
          </div>
        </div>
        <Button data-testid="account-logout-button" variant="outline" onClick={handleLogout} className="rounded-xl text-xs uppercase tracking-[0.14em] self-start">
          <LogOut className="h-3.5 w-3.5 mr-2" /> Sign out
        </Button>
      </div>

      {/* Tab strip */}
      <div className="border-b border-black/10 mb-8 -mx-1 px-1 overflow-x-auto">
        <div className="flex gap-6 min-w-max">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                data-testid={`account-tab-${t.key}`}
                onClick={() => { setTab(t.key); navigate(`/account?tab=${t.key}`, { replace: true }); }}
                className={`pb-3 flex items-center gap-2 text-xs uppercase tracking-[0.16em] transition-colors ${
                  active ? "text-[#1A1918] border-b-2 border-[#C5A059]" : "text-[#1A1918]/60 hover:text-[#1A1918]"
                }`}
              >
                <Icon className="h-3.5 w-3.5" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div className="h-40 sd-skeleton rounded-2xl" />
      ) : (
        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            data-testid={`account-panel-${tab}`}
          >
            {tab === "profile" && (
              <div className="grid md:grid-cols-2 gap-5">
                <div className="p-6 rounded-2xl border border-black/10 bg-[#F1ECE4]">
                  <div className="text-xs uppercase tracking-[0.18em] text-[#1A1918]/60 flex items-center gap-1"><Sparkles className="h-3.5 w-3.5" /> Olfactory Profile</div>
                  {profile ? (
                    <>
                      <div className="font-serif text-lg mt-3">“{profile.scent_profile}”</div>
                      <div className="mt-4 text-sm text-[#1A1918]/80">Primary Match: <strong>{profile.primary_match?.product?.name}</strong> ({profile.primary_match?.match_percentage}%)</div>
                      <Link to="/quiz" className="mt-3 inline-block text-xs uppercase tracking-[0.14em] text-[#C5A059] hover:underline">Retake Quiz</Link>
                    </>
                  ) : (
                    <div className="mt-4">
                      <div className="text-sm text-[#1A1918]/70">You haven’t taken the Scent Quiz yet.</div>
                      <Link to="/quiz" data-testid="account-take-quiz-cta" className="mt-4 inline-block px-4 py-2 rounded-xl bg-[#C5A059] text-[#1A1918] text-xs uppercase tracking-[0.16em] hover:bg-[#C5A059]/90">Take the Scent Quiz</Link>
                    </div>
                  )}
                </div>
                <div className="p-6 rounded-2xl border border-black/10 bg-[#F1ECE4]">
                  <div className="text-xs uppercase tracking-[0.18em] text-[#1A1918]/60">Account</div>
                  <div className="font-serif text-xl mt-2">{user?.full_name}</div>
                  <div className="text-sm text-[#1A1918]/70">{user?.email}</div>
                  <div className="mt-4 text-xs text-[#1A1918]/60">Currency preference: {state.currency}</div>
                </div>
              </div>
            )}

            {tab === "wardrobe" && (
              wardrobe.length === 0 ? (
                <EmptyState title="Your wardrobe is empty." body="Bottles you purchase will appear here with an estimated remaining volume." cta={{to:"/shop",label:"Explore Fragrances"}}/>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {wardrobe.map((w) => (
                    <div key={`${w.product_id}-${w.size_label}`} data-testid={`wardrobe-item-${w.product.slug}`} className="rounded-2xl border border-black/10 bg-[#F1ECE4] overflow-hidden">
                      <div className="aspect-[3/4] relative" style={{ background: `linear-gradient(160deg, ${w.product.accent_color}30, #F1ECE4)`}}>
                        <div className="absolute inset-0" style={{ backgroundImage: `url(${w.product.image_url})`, backgroundSize:"cover", backgroundPosition:"center", mixBlendMode:"multiply", opacity: 0.9 }}/>
                      </div>
                      <div className="p-4">
                        <div className="text-[10px] uppercase tracking-[0.14em] text-[#1A1918]/60">{w.product.number}</div>
                        <Link to={`/product/${w.product.slug}`} className="font-serif text-base mt-0.5 hover:text-[#C5A059]">{w.product.name}</Link>
                        <div className="text-[11px] text-[#1A1918]/60">{w.size_label} · {w.product.concentration}</div>
                        {w.engraving && <div className="mt-1 text-[10px] uppercase tracking-[0.14em] text-[#C5A059]">Engraved: {w.engraving}</div>}
                        <div className="mt-3">
                          <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.14em] text-[#1A1918]/60 mb-1">
                            <span>Approx. remaining</span><span>{w.remaining_pct}%</span>
                          </div>
                          <Progress value={w.remaining_pct} className="h-1.5 bg-[#FAF8F5] [&_[data-slot=progress-indicator]]:bg-[#C5A059]" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}

            {tab === "loyalty" && loyalty && (
              <div className="grid lg:grid-cols-[1fr_1.2fr] gap-5">
                <div className="p-6 rounded-2xl border border-[#C5A059]/30 bg-gradient-to-br from-[#F1ECE4] to-[#FAF8F5]">
                  <div className="text-xs uppercase tracking-[0.18em] text-[#1A1918]/60">Current Tier</div>
                  <div className="font-serif text-4xl mt-2 text-[#1A1918]">{loyalty.tier}</div>
                  <div className="mt-2 text-sm text-[#1A1918]/70">{loyalty.points} points · 1 point per $1 spent</div>
                  {loyalty.points_to_next > 0 && (
                    <div className="mt-6">
                      <div className="text-[10px] uppercase tracking-[0.14em] text-[#1A1918]/60 mb-1">Progress to {loyalty.next_tier}</div>
                      <Progress value={Math.max(6, ((loyalty.points % 400) / 400) * 100)} className="h-2 bg-[#FAF8F5] [&_[data-slot=progress-indicator]]:bg-[#C5A059]" data-testid="loyalty-progress" />
                      <div className="mt-1 text-[11px] text-[#1A1918]/70">{loyalty.points_to_next} points to go</div>
                    </div>
                  )}
                  <div className="mt-6">
                    <div className="text-[10px] uppercase tracking-[0.14em] text-[#1A1918]/60 mb-2">Your Perks</div>
                    <ul className="space-y-1.5">
                      {(loyalty.perks[loyalty.tier] || []).map((p) => (
                        <li key={p} className="text-sm text-[#1A1918]/85 flex gap-2"><Sparkles className="h-3.5 w-3.5 text-[#C5A059] flex-shrink-0 mt-0.5" /> {p}</li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="p-6 rounded-2xl border border-black/10 bg-[#F1ECE4]">
                  <div className="text-xs uppercase tracking-[0.18em] text-[#1A1918]/60 mb-4">Points Ledger</div>
                  {loyalty.ledger.length === 0 ? (
                    <div className="text-sm text-[#1A1918]/60">No activity yet. Your points from purchases will appear here.</div>
                  ) : (
                    <ul className="space-y-3" data-testid="loyalty-ledger-list">
                      {loyalty.ledger.map((e) => (
                        <li key={e.id} className="flex items-center justify-between text-sm border-b border-black/8 pb-3 last:border-none last:pb-0">
                          <div>
                            <div className="text-[#1A1918]">{e.reason}</div>
                            <div className="text-[11px] text-[#1A1918]/60">{new Date(e.created_at).toLocaleDateString()}</div>
                          </div>
                          <div className={`font-mono ${e.delta > 0 ? "text-[#C5A059]" : "text-red-600"}`}>{e.delta > 0 ? "+" : ""}{e.delta} pts</div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}

            {tab === "subscriptions" && (
              subs.length === 0 ? (
                <EmptyState title="No active subscriptions." body="Enable ‘Subscribe & save 15%’ on any product page to start receiving refills every 90 days." cta={{to:"/shop",label:"Browse Fragrances"}}/>
              ) : (
                <div className="space-y-3" data-testid="subscriptions-list">
                  {subs.map((s) => (
                    <div key={s.id} data-testid={`subscription-${s.id}`} className="rounded-2xl border border-black/10 bg-[#F1ECE4] p-4 sm:p-5 flex flex-col sm:flex-row items-start gap-4">
                      <div className="w-16 h-20 rounded-lg bg-[#FAF8F5]" style={{ backgroundImage: `url(${s.product?.image_url})`, backgroundSize:"cover", backgroundPosition:"center" }} />
                      <div className="flex-1">
                        <div className="text-[10px] uppercase tracking-[0.16em] text-[#1A1918]/60">{s.product?.number}</div>
                        <div className="font-serif text-lg">{s.product?.name}</div>
                        <div className="text-xs text-[#1A1918]/70">{s.size_label} · every {s.frequency_days} days · 15% off</div>
                        <div className="text-xs text-[#1A1918]/60 mt-0.5"><Calendar className="h-3 w-3 inline mr-1" /> Next: {new Date(s.next_shipment_at).toLocaleDateString()}</div>
                      </div>
                      <div className="flex flex-wrap gap-2 items-center w-full sm:w-auto">
                        <span className={`text-[10px] uppercase tracking-[0.14em] px-2.5 py-1 rounded-full ${
                          s.status === "active" ? "bg-[#C5A059]/20 text-[#C5A059]" : s.status === "paused" ? "bg-slate-200 text-slate-700" : "bg-red-100 text-red-700"
                        }`}>{s.status}</span>
                        <div className="text-sm font-medium">{format(s.discounted_price)}<span className="text-[10px] text-[#1A1918]/50 line-through ml-1">{format(s.unit_price)}</span></div>
                        {s.status !== "cancelled" && (
                          <>
                            <select
                              data-testid={`subscription-freq-${s.id}`}
                              value={s.frequency_days}
                              onChange={(e) => patchSub(s.id, { frequency_days: parseInt(e.target.value) })}
                              className="text-xs bg-[#FAF8F5] border border-black/10 rounded-md px-2 py-1"
                            >
                              <option value={30}>30 days</option>
                              <option value={60}>60 days</option>
                              <option value={90}>90 days</option>
                              <option value={120}>120 days</option>
                            </select>
                            {s.status === "active" ? (
                              <Button size="sm" variant="outline" data-testid={`subscription-pause-${s.id}`} onClick={() => patchSub(s.id, { status: "paused" })} className="rounded-full text-xs"><Pause className="h-3 w-3 mr-1" /> Pause</Button>
                            ) : (
                              <Button size="sm" variant="outline" data-testid={`subscription-resume-${s.id}`} onClick={() => patchSub(s.id, { status: "active" })} className="rounded-full text-xs"><Play className="h-3 w-3 mr-1" /> Resume</Button>
                            )}
                            <Button size="sm" variant="ghost" data-testid={`subscription-cancel-${s.id}`} onClick={() => cancelSub(s.id)} className="rounded-full text-xs text-red-700 hover:text-red-800"><CloseIcon className="h-3 w-3 mr-1" /> Cancel</Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}

            {tab === "orders" && (
              orders.length === 0 ? (
                <EmptyState title="No orders yet." body="Once you complete a purchase, your orders + tracking will appear here." cta={{to:"/shop",label:"Browse Fragrances"}}/>
              ) : (
                <div className="space-y-3" data-testid="orders-list">
                  {orders.map((o) => (
                    <Link key={o.id} to={`/order-confirmed/${o.id}`} data-testid={`order-item-${o.id}`}
                      className="block rounded-2xl border border-black/10 bg-[#F1ECE4] p-5 hover:border-[#C5A059]/40 transition-colors">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.16em] text-[#1A1918]/60">Order {o.id}</div>
                          <div className="font-serif text-base mt-0.5">{o.line_items?.length || 0} item{o.line_items?.length === 1 ? "" : "s"} · {new Date(o.created_at).toLocaleDateString()}</div>
                          <div className="text-xs text-[#1A1918]/60 mt-0.5">Tracking: {o.tracking_number}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-medium">{format(o.total)}</div>
                          <div className="text-[10px] uppercase tracking-[0.14em] text-[#C5A059] mt-1">{o.status}</div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )
            )}
          </motion.div>
        </AnimatePresence>
      )}
    </main>
  );
}

function EmptyState({ title, body, cta }) {
  return (
    <div className="py-16 text-center rounded-2xl border border-black/10 bg-[#F1ECE4]/60">
      <div className="font-serif text-2xl text-[#1A1918]">{title}</div>
      <p className="mt-2 text-sm text-[#1A1918]/70 max-w-md mx-auto">{body}</p>
      {cta && (
        <Link to={cta.to} className="mt-5 inline-block px-5 py-2.5 rounded-xl bg-[#C5A059] text-[#1A1918] text-xs uppercase tracking-[0.16em] hover:bg-[#C5A059]/90">
          {cta.label}
        </Link>
      )}
    </div>
  );
}
